import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge, Progress } from "../../components/ui-bits";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, FolderOpen } from "@phosphor-icons/react";

const CATS = ["GST", "ITR", "CMA", "TDS", "Audit", "ROC", "Payroll", "Bookkeeping", "Accounting", "Advisory", "Other"];
const CAT_COLORS = {
  GST: "#002FA7", ITR: "#00A86B", CMA: "#7C3AED", TDS: "#F5A623",
  Audit: "#DC2626", ROC: "#0891B2", Payroll: "#DB2777", Bookkeeping: "#0F766E", Accounting: "#111111", Advisory: "#4B5563", Other: "#9CA3AF",
};

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_email: "", service: "", category: "GST", due: "" });
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const load = () => {
    api.get("/admin/tasks").then((r) => setTasks(r.data));
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/agents").then((r) => setAgents(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.client_email || !form.service) { toast.error("Pick client & task"); return; }
    await api.post("/projects", form);
    toast.success("Task created");
    setForm({ client_email: "", service: "", category: "GST", due: "" }); setOpen(false); load();
  };

  const reassign = async (t, agent_email) => {
    await api.patch(`/projects/${t.id}`, { agent_email });
    toast.success("Task reassigned");
    load();
  };

  const skey = (s) => (s === "Completed" ? "completed" : s.includes("Waiting") ? "overdue" : "upcoming");
  const catOf = (t) => t.category || "Other";

  const filtered = useMemo(() => tasks.filter((t) => {
    const matchCat = filterCat === "all" || catOf(t) === filterCat;
    const s = q.toLowerCase();
    const matchQ = !s || (t.service || "").toLowerCase().includes(s) ||
      (t.company || "").toLowerCase().includes(s) || (t.client_email || "").toLowerCase().includes(s);
    return matchCat && matchQ;
  }), [tasks, filterCat, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((t) => { const c = catOf(t); (g[c] = g[c] || []).push(t); });
    return CATS.filter((c) => g[c]?.length).map((c) => [c, g[c]]);
  }, [filtered]);

  return (
    <div>
      <PageHead eyebrow="Assign & Track Work by Service" title="Task Board">
        <button data-testid="admin-new-task" onClick={() => setOpen(!open)}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors"><Plus size={16} /> New Task</button>
      </PageHead>

      {open && (
        <form onSubmit={create} className="card p-6 mb-6 grid sm:grid-cols-5 gap-3 items-end fade-up">
          <div><label className="label block mb-1">Client</label>
            <select data-testid="at-client" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="">Select…</option>
              {clients.map((c) => <option key={c.email} value={c.email}>{c.company}</option>)}
            </select></div>
          <div><label className="label block mb-1">Service Category</label>
            <select data-testid="at-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><label className="label block mb-1">Task / Job</label>
            <input data-testid="at-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <div><label className="label block mb-1">Due</label>
            <input data-testid="at-due" type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <button data-testid="at-create" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm transition-colors">Create</button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input data-testid="task-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job, client…"
            className="w-full border border-black/20 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="filter-all" onClick={() => setFilterCat("all")}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${filterCat === "all" ? "bg-[#111] text-white border-[#111]" : "border-black/20 hover:bg-black/5"}`}>All</button>
          {CATS.map((c) => (
            <button key={c} data-testid={`filter-${c.toLowerCase()}`} onClick={() => setFilterCat(c)}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${filterCat === c ? "text-white border-transparent" : "border-black/20 hover:bg-black/5"}`}
              style={filterCat === c ? { background: CAT_COLORS[c] } : {}}>{c}</button>
          ))}
        </div>
      </div>

      {grouped.length === 0 && <p className="text-sm text-[#888] card p-6">No jobs match your filters.</p>}

      <div className="space-y-6">
        {grouped.map(([cat, items]) => (
          <div key={cat} data-testid={`task-group-${cat.toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: CAT_COLORS[cat] }} />
              <FolderOpen size={16} style={{ color: CAT_COLORS[cat] }} />
              <p className="font-head font-bold text-sm tracking-wide">{cat}</p>
              <span className="text-xs text-[#888]">({items.length})</span>
            </div>
            <div className="card divide-y divide-black/10">
              {items.map((t, i) => (
                <div key={t.id} data-testid={`atask-${cat.toLowerCase()}-${i}`} className="p-4 flex flex-wrap items-center gap-4">
                  <div className="min-w-[200px] flex-1">
                    <p className="font-medium text-sm">{t.service}</p>
                    <p className="text-xs text-[#888]">{t.company || t.client_email}{t.due ? ` · Due ${t.due}` : ""}</p>
                  </div>
                  <div className="w-40"><Progress value={t.progress} /><span className="text-xs text-[#888]">{t.progress}%</span></div>
                  <Badge status={t.approval_status === "pending" ? "upcoming" : skey(t.status)}>{t.approval_status === "pending" ? "Pending Approval" : t.status}</Badge>
                  <select data-testid={`atask-agent-${cat.toLowerCase()}-${i}`} value={t.agent_email || ""} onChange={(e) => reassign(t, e.target.value)}
                    className="border border-black/20 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                    <option value="">Unassigned</option>
                    {agents.map((a) => <option key={a.email} value={a.email}>{a.name} ({a.clients ?? 0}c/{a.tasks ?? 0}j)</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
